import { ECardinality } from "@kuika/kuika-cl-model";

/**
 * The symbol picker preview's type-compatibility badge: does the value the pointer is on fit the
 * field it is about to be bound to?
 *
 *  · match       — same type (and, when both sides state it, same cardinality).
 *  · convertible — a different type the generated app converts on the way in, per
 *                  `SYMBOL_PICKER_IMPLICIT_CONVERSIONS` below ("⚠ Converted to String").
 *  · mismatch    — a list into a single value (or the reverse), or two types with no conversion.
 *
 * Unknown on either side means no verdict at all — the badge is hidden rather than guessed.
 */

export enum ESymbolPickerTypeVerdict {
  match = "match",
  convertible = "convertible",
  mismatch = "mismatch"
}

/** A value's type as the picker knows it: a type name and, when stated, its cardinality. */
export interface ISymbolPickerValueShape {
  typeName?: string;
  cardinality?: ECardinality;
}

export interface ISymbolPickerTypeCompatibility {
  verdict: ESymbolPickerTypeVerdict;
  /** The whole verdict on one badge line, glyph first: "✓ Matches String", "⚠ Converted to String". */
  label: string;
  /** A longer explanation for the badge's tooltip — set only when it says more than `label`. */
  detail?: string;
}

/** Host types that accept anything: no verdict is meaningful for them. */
const OPEN_TARGET_TYPES: ReadonlySet<string> = new Set<string>(["any", "unknown", "object"]);

const NUMERIC_TYPES: readonly string[] = ["number", "decimal", "double", "single", "float", "int16", "int32", "int64"];
const DATE_TYPES: readonly string[] = ["date", "datetime", "datetimeoffset"];

/**
 * Conversions the value takes on its way into a field of another type, keyed by the TARGET type
 * (lower case) → the source types it accepts. Hand-maintained; mirrors the families
 * `ScreenDesignHelper.getAreTypesCompatible` lets the engine offer directly:
 *
 *  · string            ← numbers, booleans, dates, guids, and the string-backed kinds
 *                        (image, file content, location, colour)
 *  · any numeric type  ← any other numeric type
 *  · any date type     ← any other date type, and text
 *  · the string-backed kinds (image, filecontent, location, colorpicker) ← string
 *
 * Anything absent here between two different types is a mismatch.
 */
export const SYMBOL_PICKER_IMPLICIT_CONVERSIONS: ReadonlyMap<string, readonly string[]> = new Map<
  string,
  readonly string[]
>([
  ["string", [...NUMERIC_TYPES, "boolean", ...DATE_TYPES, "guid", "image", "filecontent", "location", "colorpicker"]],
  ...NUMERIC_TYPES.map((numeric: string): [string, readonly string[]] => [
    numeric,
    NUMERIC_TYPES.filter((other: string): boolean => other !== numeric)
  ]),
  ...DATE_TYPES.map((date: string): [string, readonly string[]] => [
    date,
    [...DATE_TYPES.filter((other: string): boolean => other !== date), "string"]
  ]),
  ["image", ["string"]],
  ["filecontent", ["string"]],
  ["location", ["string"]],
  ["colorpicker", ["string"]]
]);

/** "System.String" → "string". Invariant: JS `toLowerCase` does not follow the host locale. */
export function normalizeSymbolPickerTypeName(typeName: string | undefined): string | undefined {
  if (!typeName) {
    return undefined;
  }
  const lastSegment: string = typeName.split(".").pop() ?? "";
  const normalized: string = lastSegment.trim().toLowerCase();
  return normalized === "" ? undefined : normalized;
}

/** The display spelling of a type: its last dotted segment, as written. */
export function displaySymbolPickerTypeName(typeName: string): string {
  return typeName.split(".").pop() ?? typeName;
}

function isList(cardinality: ECardinality | undefined): boolean {
  return cardinality === ECardinality.Many;
}

function isCardinalityStated(cardinality: ECardinality | undefined): boolean {
  return cardinality === ECardinality.One || cardinality === ECardinality.Many;
}

/**
 * The verdict for binding `source` into `target`, or undefined when either side's type is unknown or
 * the target takes anything. A source without a stated cardinality is a single value — every
 * option that is a list says so (its `cardinality`, or its "List of" caption).
 */
export function getSymbolPickerTypeCompatibility(
  source: ISymbolPickerValueShape,
  target: ISymbolPickerValueShape
): ISymbolPickerTypeCompatibility | undefined {
  const sourceType: string | undefined = normalizeSymbolPickerTypeName(source.typeName);
  const targetType: string | undefined = normalizeSymbolPickerTypeName(target.typeName);
  if (!sourceType || !targetType || !target.typeName || OPEN_TARGET_TYPES.has(targetType)) {
    return undefined;
  }
  if (OPEN_TARGET_TYPES.has(sourceType)) {
    return undefined;
  }
  const targetLabel: string = displaySymbolPickerTypeName(target.typeName);
  if (isCardinalityStated(target.cardinality)) {
    const sourceIsList: boolean = isList(source.cardinality);
    const targetIsList: boolean = isList(target.cardinality);
    if (sourceIsList && !targetIsList) {
      return {
        verdict: ESymbolPickerTypeVerdict.mismatch,
        label: "✗ List into a single value",
        detail: `A list cannot go into a single ${targetLabel}.`
      };
    }
    if (!sourceIsList && targetIsList) {
      return {
        verdict: ESymbolPickerTypeVerdict.mismatch,
        label: "✗ Single value into a list",
        detail: `A single value cannot fill a list of ${targetLabel}.`
      };
    }
  }
  if (sourceType === targetType) {
    return { verdict: ESymbolPickerTypeVerdict.match, label: `✓ Matches ${targetLabel}` };
  }
  const accepted: readonly string[] | undefined = SYMBOL_PICKER_IMPLICIT_CONVERSIONS.get(targetType);
  if (accepted?.includes(sourceType)) {
    return {
      verdict: ESymbolPickerTypeVerdict.convertible,
      label: `⚠ Converted to ${targetLabel}`
    };
  }
  return {
    verdict: ESymbolPickerTypeVerdict.mismatch,
    label: `✗ ${displaySymbolPickerTypeName(source.typeName ?? sourceType)} doesn't fit ${targetLabel}`
  };
}
